/**
 * Formatos es-CL (Intl). Las fechas se muestran en hora de Chile continental aunque
 * el navegador del analista esté en otra zona: los casos y los avisos son chilenos y
 * un "visto el 03-10" no debe cambiar según dónde se abra la app.
 */

const LOCALE = "es-CL";
const TZ = "America/Santiago";

const clp = new Intl.NumberFormat(LOCALE, { style: "currency", currency: "CLP", maximumFractionDigits: 0 });
const int = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const dec1 = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pct = new Intl.NumberFormat(LOCALE, { style: "percent", maximumFractionDigits: 0 });
const date = new Intl.DateTimeFormat(LOCALE, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: TZ });
const dateLong = new Intl.DateTimeFormat(LOCALE, { day: "numeric", month: "long", year: "numeric", timeZone: TZ });
const dateTime = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: TZ,
});
const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });

function toDate(v: string | Date): Date | null {
  const d = typeof v === "string" ? new Date(v) : v;
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtCLP(v: number | null | undefined): string {
  return v === null || v === undefined ? "Sin precio" : clp.format(v);
}

export function fmtInt(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : int.format(v);
}

export function fmtDecimal(v: number): string {
  return dec1.format(v);
}

export function fmtPercent(ratio: number): string {
  return pct.format(ratio);
}

export function fmtDate(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = toDate(v);
  return d ? date.format(d) : "—";
}

export function fmtDateLong(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = toDate(v);
  return d ? dateLong.format(d) : "—";
}

export function fmtDateTime(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = toDate(v);
  return d ? dateTime.format(d) : "—";
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "hace 3 días", "hace 5 minutos", "en 2 horas"… */
export function fmtRelative(v: string | Date | null | undefined, now: Date = new Date()): string {
  if (!v) return "—";
  const d = toDate(v);
  if (!d) return "—";
  const diffSec = (d.getTime() - now.getTime()) / 1000;
  const abs = Math.abs(diffSec);
  for (const [unit, secs] of UNITS) {
    if (abs >= secs) return rtf.format(Math.round(diffSec / secs), unit);
  }
  return "recién";
}

/** Duración en ms → "850 ms" / "1,2 s". */
export function fmtMs(ms: number): string {
  return ms < 1000 ? `${int.format(ms)} ms` : `${dec1.format(ms / 1000)} s`;
}

/** Cuenta regresiva mm:ss para bloqueos (429). */
export function fmtCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** Fecha local de Chile en formato YYYY-MM-DD (para inputs type=date y filtros). */
export function isoDay(d: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: TZ })
    .formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function daysAgo(n: number, from: Date = new Date()): Date {
  return new Date(from.getTime() - n * 24 * 3600 * 1000);
}

/** RUT chileno con puntos y guion: 12345678 + 5 → "12.345.678-5". */
export function fmtRut(body: string, dv: string): string {
  return `${int.format(Number(body))}-${dv.toUpperCase()}`;
}

/** Teléfono canónico (9 dígitos) → "+56 9 1234 5678". */
export function fmtPhone(p: string): string {
  if (/^9\d{8}$/.test(p)) return `+56 9 ${p.slice(1, 5)} ${p.slice(5)}`;
  if (/^\d{9}$/.test(p)) return `+56 ${p.slice(0, 2)} ${p.slice(2, 5)} ${p.slice(5)}`;
  return p;
}

/** Patente con guiones para lectura humana: BBCL12 → BB·CL·12. */
export function fmtPlate(p: string): string {
  const v = p.toUpperCase();
  if (/^[A-Z]{4}\d{2}$/.test(v)) return `${v.slice(0, 2)}·${v.slice(2, 4)}·${v.slice(4)}`;
  if (/^[A-Z]{2}\d{4}$/.test(v)) return `${v.slice(0, 2)}·${v.slice(2, 4)}·${v.slice(4)}`;
  return v;
}

const UPPER_WORDS = new Set([
  "bmw", "mg", "jac", "baic", "byd", "gwm", "dfsk", "ds", "kgm", "jmc", "faw", "gac", "jmev", "dfm",
  "suv", "gt", "gti", "gls", "glx", "xli", "ex", "lx", "rs", "amg", "tdi", "crdi", "4x4", "4x2", "awd", "ii", "iii", "iv",
]);

/** Valores canónicos del catálogo ("toyota", "cx-5", "mercedes-benz") → texto para mostrar. */
export function titleCase(v: string | null | undefined): string {
  if (!v) return "";
  return v
    .split(/(\s+|-)/)
    .map((w) => {
      if (/^\s+$/.test(w) || w === "-") return w;
      const lw = w.toLowerCase();
      if (UPPER_WORDS.has(lw) || /\d/.test(w)) return w.toUpperCase();
      return lw.charAt(0).toUpperCase() + lw.slice(1);
    })
    .join("");
}

/** "Juan Pérez" → "JP" para el avatar del menú de usuario. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

export function plural(n: number, singular: string, pluralForm: string): string {
  return `${fmtInt(n)} ${n === 1 ? singular : pluralForm}`;
}

/** "$ 5.500.000" / "5500000" → 5500000 (los precios se escriben con puntos de miles en Chile). */
export function parseIntLoose(v: string): number | null {
  const digits = v.replace(/[\s.$]/g, "");
  if (!/^\d+$/.test(digits)) return null;
  const n = Number(digits);
  return Number.isSafeInteger(n) ? n : null;
}
